import { useEffect, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { gbp, longDate } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

/** The property's selection criteria, in a popup over the apply page instead of a new tab. */
export const CriteriaDialog = ({ token, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || data) return;
    api.get(`/public/criteria/${token}`).then((r) => setData(r.data)).catch((err) => setError(errorText(err)));
  }, [open, data, token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="criteria-dialog">
        <DialogTitle>How tenants are selected</DialogTitle>
        <DialogDescription className="sr-only">The rules every applicant for this property is checked against.</DialogDescription>
        {error && <p className="rounded-xl bg-destructive/10 p-4 text-sm text-destructive" data-testid="criteria-dialog-error">{error}</p>}
        {!data && !error && <Loader2 className="mx-auto my-6 size-5 animate-spin text-muted-foreground" />}
        {data && (
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-base font-semibold" data-testid="criteria-dialog-address">{data.address}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {gbp(data.monthly_rent)} a month · move in {longDate(data.move_in_date)} · {data.tenants} {data.tenants === 1 ? "tenant" : "tenants"} · let by {data.agency_name}
              </p>
            </div>
            <ul className="flex flex-col gap-3 rounded-xl border p-4">
              {data.plain_english.map((line, i) => (
                <li key={i} className="flex gap-3 text-sm" data-testid={`criteria-dialog-line-${i}`}>
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-foreground/40" />
                  <span className="leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-xl bg-brand/40 p-4 dark:bg-brand/10">
              <p className="flex items-center gap-2 text-sm font-semibold"><Scale className="size-4" /> Fair by design</p>
              <p className="mt-1 text-sm leading-relaxed">
                Documents are checked by fixed rules, not by a person's impression. The agent first sees a blind summary with no name, photo, age, nationality, household or income source.
              </p>
            </div>
            <p className="font-mono text-xs text-muted-foreground">Criteria version {data.criteria_version} · published {longDate(data.published_at)}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
